import React, { useEffect, useRef, useCallback } from "react";
import styles from "./styles/carousel.module.css";

interface HealthProblemsCarouselProps {
  images: string[];
}

const HealthProblemsCarousel: React.FC<HealthProblemsCarouselProps> = ({
  images,
}) => {
  const tripled = [...images, ...images, ...images];

  const trackRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const rafRef = useRef<number>(0);

  const SPEED = 0.4;

  const animate = useCallback(() => {
    const track = trackRef.current;
    const wrapper = wrapperRef.current;
    if (!track || !wrapper) {
      rafRef.current = requestAnimationFrame(animate);
      return;
    }

    offsetRef.current += SPEED;

    const oneSetWidth = track.scrollWidth / 3;

    if (offsetRef.current >= oneSetWidth) {
      offsetRef.current -= oneSetWidth;
    }

    track.style.transform = `translateX(-${offsetRef.current}px)`;

    const wrapperRect = wrapper.getBoundingClientRect();
    const centerX = wrapperRect.left + wrapperRect.width / 2;
    const maxDist = wrapperRect.width / 2;

    const items = track.children;
    for (let i = 0; i < items.length; i++) {
      const el = items[i] as HTMLElement;
      const elRect = el.getBoundingClientRect();
      const elCenterX = elRect.left + elRect.width / 2;
      const dist = Math.abs(centerX - elCenterX);

      const t = Math.min(dist / maxDist, 1);
      const scale = 1.15 - t * 0.35;
      const opacity = 1 - t * 0.35;

      el.style.transform = `scale(${scale})`;
      el.style.opacity = `${opacity}`;
    }

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [animate]);

  return (
    <div
      className={`${styles.carouselWrapper} hidden md:flex md:items-center`}
      ref={wrapperRef}
    >
      <div className={styles.carouselTrack} ref={trackRef}>
        {tripled.map((src, idx) => (
          <div key={idx} className={styles.carouselItem}>
            <img src={src} alt="Health problem aspect" />
          </div>
        ))}
      </div>
    </div>
  );
};

export default HealthProblemsCarousel;
